import React, { useMemo, useState } from "react";
import { FiStar } from "react-icons/fi";
import Dropdown, { DropdownItem, DropdownText, DropdownDivider } from "./Dropdown";
import { useI18n } from "../../i18n/I18nContext";
import styles from "./Styles/MultiSelect.module.css";

// Single-select autocomplete dropdown, styled like MultiSelect (search box
// narrows the option list) but picking an option selects it and closes the
// panel right away, instead of staying open for picking several.
// An option is { value, label } plus an optional `secondary` string shown
// after the label in smaller, dimmer text (e.g. the customer behind a
// project) - the search matches either one.
// Pass onToggleFavorite(value) to turn on starring: every option then gets a
// star button and the ones flagged `favorite` are pinned above the rest.
const AutocompleteSelect = ({ label, placeholder, options, value, onChange, disabled, onToggleFavorite, className = "" }) => {
  const { t } = useI18n();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.secondary || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  // Starred options come first, both groups alphabetical - including while
  // typing, so a favorite that matches stays on top. Pickers without
  // starring keep the order they were handed, untouched.
  const [favorites, rest] = useMemo(() => {
    if (!onToggleFavorite) return [[], filtered];
    const byLabel = (a, b) => a.label.localeCompare(b.label);
    return [filtered.filter((o) => o.favorite).sort(byLabel), filtered.filter((o) => !o.favorite).sort(byLabel)];
  }, [filtered, onToggleFavorite]);

  const selected = options.find((o) => o.value === value);

  return (
    <Dropdown
      title={label}
      className={className}
      triggerClassName={styles.trigger}
      disabled={disabled}
      trigger={
        <>
          <span className={styles.triggerLabel}>{label}</span>
          <span className={styles.triggerSummary}>
            {selected ? selected.label : placeholder}
            {selected && selected.secondary && (
              <span className={styles.secondary}>{selected.secondary}</span>
            )}
          </span>
        </>
      }
    >
      {(close) => {
        // The star sits inside the option row, which is itself a button, so
        // it's a span with a button role rather than a nested <button>, and
        // it stops the click from selecting the option it belongs to.
        const toggleFavorite = (e, option) => {
          e.stopPropagation();
          e.preventDefault();
          onToggleFavorite(option.value);
        };

        const renderOption = (o) => (
          <DropdownItem
            key={o.value}
            active={o.value === value}
            onClick={() => {
              onChange(o.value);
              setQuery("");
              close();
            }}
          >
            <span className={styles.optionLabel}>{o.label}</span>
            {o.secondary && <span className={styles.secondary}>{o.secondary}</span>}
            {onToggleFavorite && (
              <span
                role="button"
                tabIndex={0}
                aria-pressed={!!o.favorite}
                aria-label={t(o.favorite ? "common.removeFavorite" : "common.addFavorite")}
                title={t(o.favorite ? "common.removeFavorite" : "common.addFavorite")}
                className={`${styles.star} ${o.favorite ? styles.starOn : ""}`}
                onClick={(e) => toggleFavorite(e, o)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleFavorite(e, o)}
              >
                <FiStar fontSize="15px" />
              </span>
            )}
          </DropdownItem>
        );

        return (
          <div className={styles.panel}>
            <input
              className={`cw-input ${styles.search}`}
              type="text"
              placeholder={t("common.search")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <div className={styles.list}>
              {filtered.length === 0 && <DropdownText>{t("common.noResults")}</DropdownText>}
              {favorites.map(renderOption)}
              {favorites.length > 0 && rest.length > 0 && <DropdownDivider />}
              {rest.map(renderOption)}
            </div>
            {value && (
              <button
                type="button"
                className={styles.clear}
                onClick={() => {
                  onChange("");
                  setQuery("");
                  close();
                }}
              >
                {t("common.clearFilter")}
              </button>
            )}
          </div>
        );
      }}
    </Dropdown>
  );
};

export default AutocompleteSelect;
